<script lang="ts">
  import { onMount } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import { getBudgetStatus } from '../lib/storage.js';
  import type { Summary } from '../lib/types.js';

  type BudgetStatus = {
    name: string;
    categories: string[];
    limit: number;
    spent: number;
    type: 'monthly' | 'quarterly' | 'yearly';
  };

  type Props = {
    summary: Summary;
  };

  let { summary }: Props = $props();
  let canvas: HTMLCanvasElement;
  let chart: Chart | null = null;
  let loading = $state(true);
  let error = $state<string | null>(null);

  onMount(() => {
    Chart.register(...registerables);

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  });

  // Re-render chart when summary changes or canvas is available
  $effect(() => {
    if (canvas && summary) {
      renderChart();
    }
  });

  async function renderChart() {
    loading = true;
    error = null;

    console.log('BudgetVsActual: renderChart called, canvas element:', canvas);

    // Wait for the canvas element to be available
    if (!canvas) {
      console.error('BudgetVsActual: Canvas element not available');
      error = 'Chart canvas not available';
      loading = false;
      return;
    }

    // Destroy existing chart before creating new one
    if (chart) {
      try {
        chart.destroy();
      } catch (e) {
        console.warn('Chart destroy failed:', e);
      }
      chart = null;
    }

    // Also check if Chart.js has an existing chart instance for this canvas
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      try {
        existingChart.destroy();
      } catch (e) {
        console.warn('Existing chart destroy failed:', e);
      }
    }

    try {
      // Budget status is fully implemented in storage provider
      // Get current month for budget status
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      console.log('BudgetVsActual: Fetching budget status for month:', currentMonth);

      // Get budget status from storage provider
      const data = await getBudgetStatus(currentMonth);
      console.log('BudgetVsActual: Received data:', data);

      const monthlyBudgets: BudgetStatus[] = data.monthly || [];
      console.log('BudgetVsActual: Monthly budgets:', monthlyBudgets);

      if (!monthlyBudgets || monthlyBudgets.length === 0) {
        error = 'No budget data available';
        loading = false;
        return;
      }

      // Prepare data for chart - show all budgets
      const labels: string[] = [];
      const underBudgetData: number[] = [];
      const overBudgetData: number[] = [];
      const remainingBudgetData: number[] = [];

      // For each budget, calculate the breakdown
      monthlyBudgets.forEach((budget: BudgetStatus) => {
        labels.push(budget.name);

        if (budget.spent <= budget.limit) {
          // Under budget: show spent (green) and remaining (gray)
          underBudgetData.push(budget.spent);
          overBudgetData.push(0);
          remainingBudgetData.push(budget.limit - budget.spent);
        } else {
          // Over budget: show budget limit (green), then overage (red)
          underBudgetData.push(budget.limit);
          overBudgetData.push(budget.spent - budget.limit);
          remainingBudgetData.push(0);
        }
      });

      console.log('BudgetVsActual: Chart data:', { labels, underBudgetData, overBudgetData, remainingBudgetData });

      if (labels.length === 0) {
        error = 'No spending data for current month';
        loading = false;
        return;
      }

      // Get CSS variable colors
      const successColor = getComputedStyle(document.documentElement).getPropertyValue('--color-success').trim() || '#10b981';
      const dangerColor = getComputedStyle(document.documentElement).getPropertyValue('--color-danger').trim() || '#ef4444';
      const grayColor = getComputedStyle(document.documentElement).getPropertyValue('--color-border-medium').trim() || '#d1d5db';

      console.log('BudgetVsActual: Creating chart with canvas:', canvas);

      // Create the chart
      chart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Spent',
              data: underBudgetData,
              backgroundColor: successColor,
              borderColor: successColor,
              borderWidth: 0,
            },
            {
              label: 'Over Budget',
              data: overBudgetData,
              backgroundColor: dangerColor,
              borderColor: dangerColor,
              borderWidth: 0,
            },
            {
              label: 'Remaining',
              data: remainingBudgetData,
              backgroundColor: grayColor,
              borderColor: grayColor,
              borderWidth: 0,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          aspectRatio: 2,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-tertiary').trim(),
                font: {
                  size: 12
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  label += new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  }).format(context.parsed.y);
                  return label;
                },
                footer: function(tooltipItems) {
                  const index = tooltipItems[0].dataIndex;
                  const budget = monthlyBudgets[index];
                  const percentage = budget.limit > 0 ? ((budget.spent / budget.limit) * 100).toFixed(1) : 0;

                  return [
                    `Budget: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(budget.limit)}`,
                    `Spent: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(budget.spent)}`,
                    `Usage: ${percentage}%`
                  ];
                }
              }
            }
          },
          scales: {
            x: {
              stacked: true,
              ticks: {
                color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-tertiary').trim(),
                maxRotation: 45,
                minRotation: 0
              },
              grid: {
                display: false
              }
            },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: {
                color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-tertiary').trim(),
                callback: function(value) {
                  return '$' + value.toLocaleString();
                }
              },
              grid: {
                color: getComputedStyle(document.documentElement).getPropertyValue('--color-border').trim()
              }
            }
          }
        }
      });

      console.log('BudgetVsActual: Chart created successfully:', chart);

      loading = false;
    } catch (err: any) {
      console.error('Error loading budget data:', err);
      error = err.message || 'Failed to load budget data';
      loading = false;
    }
  }
</script>

<div class="chart-wrapper">
  {#if loading}
    <div class="chart-loading">Loading budget data...</div>
  {/if}

  {#if error}
    <div class="chart-error">{error}</div>
  {/if}

  <div class="chart-container" style:display={loading || error ? 'none' : 'block'}>
    <canvas bind:this={canvas}></canvas>
  </div>
</div>

<style>
  .chart-wrapper {
    position: relative;
    width: 100%;
    min-height: 300px;
  }

  .chart-container {
    position: relative;
    width: 100%;
    height: 300px;
  }

  .chart-loading,
  .chart-error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .chart-error {
    color: var(--color-danger);
  }
</style>
